import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Heart, ShoppingBag, SlidersHorizontal, ChevronDown, X, ArrowLeft, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import Navbar from './Navbar';

const ProductList = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const { t, API, token, toggleFavorite, isFavorite, addToCart } = useApp();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [fragranceFamilies, setFragranceFamilies] = useState([]);
  
  // Filters
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedFragrance, setSelectedFragrance] = useState('');
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadProducts();
    loadFilters();
  }, [category, selectedBrand, selectedFragrance, sortBy]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (selectedBrand) params.append('brand', selectedBrand);
      if (selectedFragrance) params.append('fragrance_family', selectedFragrance);
      params.append('sort', sortBy);
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFilters = async () => {
    try {
      const [brandsRes, familiesRes] = await Promise.all([
        axios.get(`${API}/products/brands/list`),
        axios.get(`${API}/products/fragrance-families/list`)
      ]);
      setBrands(brandsRes.data);
      setFragranceFamilies(familiesRes.data);
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  };

  const handleFavoriteClick = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!token) {
      navigate('/login');
      return;
    }
    await toggleFavorite(productId);
  };

  const getStockStatus = (stock) => {
    if (stock <= 0) return { text: t('out_of_stock'), color: 'text-red-500' };
    if (stock <= 5) return { text: t('low_stock'), color: 'text-orange-500' };
    return { text: t('in_stock'), color: 'text-green-500' };
  };

  const oppositeCategory = category === 'men' ? 'women' : 'men';

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      {/* Header */}
      <div 
        className="relative h-64 md:h-80 overflow-hidden"
        style={{
          backgroundImage: category === 'men' 
            ? 'url(https://images.unsplash.com/photo-1519761192872-d8496d4ccaaf?w=1920)'
            : 'url(https://images.pexels.com/photos/12053223/pexels-photo-12053223.jpeg?w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/60 to-transparent" />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <h1 className="font-heading text-4xl md:text-6xl text-white mb-4">
            {category === 'men' ? t('perfume_for_men') : t('perfume_for_women')}
          </h1>
          <button
            onClick={() => navigate(`/products/${oppositeCategory}`)}
            className="flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors"
            data-testid="switch-category-btn"
          >
            {category === 'men' ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
            <span className="font-accent text-sm tracking-wider">
              {oppositeCategory === 'men' ? t('perfume_for_men') : t('perfume_for_women')}
            </span>
            {category === 'men' ? null : <ArrowRight size={18} />}
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Filters Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary flex items-center gap-2"
              data-testid="filter-toggle-btn"
            >
              <SlidersHorizontal size={18} />
              Filters
            </button>
            
            {/* Quick Filters */}
            <div className="hidden md:flex items-center gap-4">
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                data-testid="brand-filter"
              >
                <option value="">{t('filter_brand')}</option>
                {brands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>

              <select
                value={selectedFragrance}
                onChange={(e) => setSelectedFragrance(e.target.value)}
                className="bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                data-testid="fragrance-filter"
              >
                <option value="">{t('filter_fragrance')}</option>
                {fragranceFamilies.map(family => (
                  <option key={family} value={family}>{family}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
            data-testid="sort-select"
          >
            <option value="newest">{t('sort_newest')}</option>
            <option value="price_low">{t('sort_price_low')}</option>
            <option value="price_high">{t('sort_price_high')}</option>
          </select>
        </div>

        {/* Mobile Filters Panel */}
        {showFilters && (
          <div className="md:hidden glass p-4 rounded-xl mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading text-lg text-white">Filters</h3>
              <button onClick={() => setShowFilters(false)}>
                <X size={20} className="text-[#A8A29E]" />
              </button>
            </div>
            <div className="space-y-4">
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-2 text-white"
              >
                <option value="">{t('filter_brand')}</option>
                {brands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
              <select
                value={selectedFragrance}
                onChange={(e) => setSelectedFragrance(e.target.value)}
                className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-2 text-white"
              >
                <option value="">{t('filter_fragrance')}</option>
                {fragranceFamilies.map(family => (
                  <option key={family} value={family}>{family}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {products.map((product) => {
              const stockStatus = getStockStatus(product.stock);
              const discount = product.original_price 
                ? Math.round((1 - product.price / product.original_price) * 100) 
                : 0;

              return (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="product-card group"
                  data-testid={`product-card-${product.id}`}
                >
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-[#121212]">
                    {/* Product Image */}
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    
                    {/* Favorite Button */}
                    <button
                      onClick={(e) => handleFavoriteClick(e, product.id)}
                      className={`favorite-btn absolute top-3 right-3 p-2 rounded-full glass ${
                        isFavorite(product.id) ? 'opacity-100' : ''
                      }`}
                      data-testid={`favorite-btn-${product.id}`}
                    >
                      <Heart 
                        size={20} 
                        className={isFavorite(product.id) ? 'fill-red-500 text-red-500' : 'text-white'} 
                      />
                    </button>

                    {/* Discount Badge */}
                    {discount > 0 && (
                      <div className="absolute top-3 left-3 bg-[#D4AF37] text-black px-2 py-1 rounded text-xs font-bold">
                        -{discount}%
                      </div>
                    )}

                    {/* Product Info */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-[#1C1917] bg-[#D4AF37]/90 inline-block px-2 py-0.5 rounded text-xs uppercase tracking-wider mb-1 font-bold">
                        {product.brand}
                      </p>
                      <h3 className="font-heading text-white text-lg mb-2 line-clamp-1">
                        {product.name}
                      </h3>
                      
                      {/* Price */}
                      <div className="flex items-center gap-2">
                        <span className="text-[#D4AF37] font-bold">
                          €{product.price.toFixed(2)}
                        </span>
                        {product.original_price && (
                          <span className="text-[#1C1917] bg-white/80 px-1 rounded text-sm line-through font-medium">
                            €{product.original_price.toFixed(2)}
                          </span>
                        )}
                      </div>

                      {/* Stock Status */}
                      <p className={`text-xs mt-2 ${stockStatus.color}`}>
                        {stockStatus.text}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {products.length === 0 && !loading && (
          <div className="text-center py-20">
            <p className="text-[#A8A29E] text-lg">No products found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductList;
